import { useEffect } from 'react'

import {
  HANDOFF_ONBOARD_KEY,
  HANDOFF_REGISTER_KEY,
  HANDOFF_TRIAGE_KEY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { TRIAGE_ENTRY_POINTS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  DirectLinkSource,
  SEARCHER_TYPE_TO_MATCH_FOR,
} from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { TOOLKIT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/types'

import { useAppDispatch } from '../../../data/storeConfiguration/store'
import { setDirectLinkSource, setToolkitType } from '../../onboard/data/onboardDataActions'
import { updateTriageDemographics } from '../../onboard/data/triage-demographics/triage-demographics-actions'
import { setTriageEntryPoint } from '../../register/data/registerActions'
import {
  clearTriage,
  setIsSearchToolkitAware,
  setPreselectedTriageData,
  setProgramPreference,
  setSearcherInfo,
  setToolkitSubTopic,
  setTreatmentRecommendations,
} from '../data/triageActions'

/**
 * Reads handoff keys written to sessionStorage by the 1Lyra middleware before navigating to LW,
 * dispatches the corresponding Redux actions to pre-populate triage, onboard, and register state,
 * then removes the keys. This bypasses the redux-persist debounce race condition that would
 * otherwise drop state written immediately before a window.location.href navigation.
 */
export const use1LyraHandoff = () => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    const hasAnyHandoffKey =
      sessionStorage.getItem(HANDOFF_TRIAGE_KEY) !== null ||
      sessionStorage.getItem(HANDOFF_ONBOARD_KEY) !== null ||
      sessionStorage.getItem(HANDOFF_REGISTER_KEY) !== null

    // Clear stale triage state (e.g. selectedSearchId) that LW's redux-persist may have
    // restored from a previous search before applying the fresh handoff values from 1Lyra.
    if (hasAnyHandoffKey) {
      dispatch(clearTriage())
    }

    try {
      const rawTriage = sessionStorage.getItem(HANDOFF_TRIAGE_KEY)
      if (rawTriage) {
        const triage = JSON.parse(rawTriage)
        if (triage.matchFor !== undefined) {
          dispatch(updateTriageDemographics('matchFor', triage.matchFor as SEARCHER_TYPE_TO_MATCH_FOR))
        } else if (triage.searcherInfo !== undefined) {
          dispatch(updateTriageDemographics('matchFor', SEARCHER_TYPE_TO_MATCH_FOR.SELF))
        }
        if (triage.searcherInfo !== undefined) dispatch(setSearcherInfo(triage.searcherInfo))
        if (triage.treatmentRecommendations !== undefined)
          dispatch(setTreatmentRecommendations(triage.treatmentRecommendations))
        if (triage.isSearchToolkitAware !== undefined) dispatch(setIsSearchToolkitAware(triage.isSearchToolkitAware))
        if (triage.toolkitSubTopic !== undefined) dispatch(setToolkitSubTopic(triage.toolkitSubTopic))
        if (triage.programPreference !== undefined) dispatch(setProgramPreference(triage.programPreference))
        if (triage.preselectedTriageData !== undefined) dispatch(setPreselectedTriageData(triage.preselectedTriageData))
        sessionStorage.removeItem(HANDOFF_TRIAGE_KEY)
      }
    } catch {
      sessionStorage.removeItem(HANDOFF_TRIAGE_KEY)
    }

    try {
      const rawOnboard = sessionStorage.getItem(HANDOFF_ONBOARD_KEY)
      if (rawOnboard) {
        const onboard = JSON.parse(rawOnboard)
        if (onboard.data?.toolkitType !== undefined) dispatch(setToolkitType(onboard.data.toolkitType as TOOLKIT_TYPE))
        if (onboard.data?.directLinkSource !== undefined)
          dispatch(setDirectLinkSource(onboard.data.directLinkSource as DirectLinkSource))
        sessionStorage.removeItem(HANDOFF_ONBOARD_KEY)
      }
    } catch {
      sessionStorage.removeItem(HANDOFF_ONBOARD_KEY)
    }

    try {
      const rawRegister = sessionStorage.getItem(HANDOFF_REGISTER_KEY)
      if (rawRegister) {
        const register = JSON.parse(rawRegister)
        if (register.triageEntrypoint !== undefined)
          dispatch(setTriageEntryPoint(register.triageEntrypoint as TRIAGE_ENTRY_POINTS))
        sessionStorage.removeItem(HANDOFF_REGISTER_KEY)
      }
    } catch {
      sessionStorage.removeItem(HANDOFF_REGISTER_KEY)
    }
  }, [dispatch])
}
